/**
 * Curated language picklist for tutor profile (not full ISO 639-1 — avoids ultra-rare codes).
 * Labels: UI locale → English `Intl.DisplayNames` → `iso-639-1` English name.
 * Popular order: en → pl → uk → ru → es → de → fr → it → cs (when present in curated list).
 */

import ISO6391 from 'iso-639-1';

export type LanguageSection = 'popular' | 'all';

export type LanguageOption = {
  /** Lowercase ISO 639-1 alpha-2 */
  code: string;
  /** Display name only */
  label: string;
  section: LanguageSection;
};

/**
 * Popular order (fixed). Only codes present in {@link CURATED_TUTOR_LANGUAGE_CODES} are shown.
 */
const POPULAR_ORDERED: readonly string[] = ['en', 'pl', 'uk', 'ru', 'es', 'de', 'fr', 'it', 'cs'];

/**
 * Practical subset for music / tutoring (EU + widely spoken languages).
 * ~50 codes — no `supportedValuesOf` / full ISO list.
 */
const CURATED_TUTOR_LANGUAGE_CODES: readonly string[] = Array.from(
  new Set([
    'af',
    'ar',
    'az',
    'be',
    'bg',
    'bn',
    'bs',
    'ca',
    'cs',
    'cy',
    'da',
    'de',
    'el',
    'en',
    'es',
    'et',
    'eu',
    'fa',
    'fi',
    'fo',
    'fr',
    'ga',
    'gl',
    'he',
    'hi',
    'hr',
    'hu',
    'hy',
    'id',
    'is',
    'it',
    'ja',
    'ka',
    'ko',
    'lb',
    'lt',
    'lv',
    'mk',
    'ms',
    'nl',
    'nb',
    'pl',
    'pt',
    'ro',
    'ru',
    'sk',
    'sl',
    'sq',
    'sr',
    'sv',
    'sw',
    'ta',
    'th',
    'tl',
    'tr',
    'uk',
    'ur',
    'vi',
    'zh',
  ])
).sort();

function isUselessDisplayName(name: string | undefined, code: string): boolean {
  if (name == null || name.trim() === '') return true;
  const n = name.trim().toLowerCase();
  const c = code.toLowerCase();
  return n === c;
}

function displayNameForLocale(code: string, locale: string): string | undefined {
  try {
    const dn = new Intl.DisplayNames([locale.replace(/_/g, '-')], { type: 'language' });
    const n = dn.of(code);
    if (!isUselessDisplayName(n, code)) return n ?? undefined;
  } catch {
    /* ignore */
  }
  return undefined;
}

/**
 * Resolve a human-readable language name for UI.
 */
export function resolveLanguageLabel(code: string, uiLocale: string): string {
  const primary = uiLocale.replace(/_/g, '-');
  const fromUi = displayNameForLocale(code, primary);
  const fromEn = displayNameForLocale(code, 'en');
  const fromIso = ISO6391.getName(code);
  return (
    fromUi ??
    fromEn ??
    (fromIso && fromIso.length > 0 ? fromIso : undefined) ??
    code.toUpperCase()
  );
}

function getPopularCodes(codeSet: Set<string>): string[] {
  return POPULAR_ORDERED.filter((c) => codeSet.has(c));
}

/** When CLDR gives the same name for two codes, disambiguate with the ISO code. */
function disambiguateDuplicateLabels(options: LanguageOption[]): LanguageOption[] {
  const byNorm = new Map<string, LanguageOption[]>();
  for (const o of options) {
    const k = o.label.trim().toLowerCase();
    if (!byNorm.has(k)) byNorm.set(k, []);
    byNorm.get(k)!.push(o);
  }
  return options.map((o) => {
    const group = byNorm.get(o.label.trim().toLowerCase())!;
    if (group.length > 1) {
      return { ...o, label: `${o.label} (${o.code})` };
    }
    return o;
  });
}

/**
 * @param uiLocale — e.g. `i18n.language` from react-i18next (`pl`, `en-US`, …)
 */
export function getLanguageOptions(uiLocale: string): LanguageOption[] {
  const codes = [...CURATED_TUTOR_LANGUAGE_CODES];
  const codeSet = new Set(codes);
  const primary = uiLocale.replace(/_/g, '-');

  const popularCodes = getPopularCodes(codeSet);
  const popularSet = new Set(popularCodes);

  const popularOptions: LanguageOption[] = popularCodes.map((code) => ({
    code,
    label: resolveLanguageLabel(code, uiLocale),
    section: 'popular' as const,
  }));

  const otherCodes = codes.filter((c) => !popularSet.has(c));
  const otherOptions: LanguageOption[] = otherCodes.map((code) => ({
    code,
    label: resolveLanguageLabel(code, uiLocale),
    section: 'all' as const,
  }));

  otherOptions.sort((a, b) => a.label.localeCompare(b.label, primary, { sensitivity: 'base' }));

  return disambiguateDuplicateLabels([...popularOptions, ...otherOptions]);
}
